import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "./managers/AuthContext";
import { KnockoutProvider } from "./managers/KnockoutContext";
import Layout from "./components/Layout";

import Home from "./screens/Home";
import Login from "./screens/Login";
import Draft from "./screens/Draft";
import DraftComplete from "./screens/DraftComplete";
import Leaderboard from "./screens/Leaderboard";
import Profile from "./screens/Profile";
import RosterBattles from "./screens/RosterBattles";
import ModeRules from "./screens/ModeRules";
import Knockout from "./screens/Knockout";
import KnockoutDraft from "./screens/KnockoutDraft";
import KnockoutComplete from "./screens/KnockoutComplete";
import PickUp from "./screens/PickUp";
import Friends from "./screens/Friends";
import PickUpMatchmaking from "./screens/PickUpMatchmaking";
import PickUpDraft from "./screens/PickUpDraft";
import PickUpComplete from "./screens/PickUpComplete";
import PickUpCpu from "./screens/PickUpCpu";
import StreakOverview from "./screens/StreakOverview";
import Streak from "./screens/Streak";
import StreakComplete from "./screens/StreakComplete";
import DraftOverview from "./screens/DraftOverview";
import Terms from "./screens/Terms";
import Privacy from "./screens/Privacy";
import Support from "./screens/Support";
import Showcase from "./screens/Showcase";

// Restores session before rendering any screen.
// Shows nothing while session is being read from localStorage.
function AuthGate({ children }) {
  const { isRestoring } = useAuth();
  if (isRestoring) return null;
  return children;
}

function AppRoutes() {
  return (
    <AuthGate>
      <KnockoutProvider>
        <Layout>
          <Routes>
            {/* Public */}
            <Route path="/login"   element={<Login />} />
            <Route path="/terms-of-service" element={<Terms />} />
            <Route path="/privacy-policy"   element={<Privacy />} />
            <Route path="/support"          element={<Support />} />

            {/* Open — no login required. New users are nudged to sign in
                before starting a draft (see DraftOverview), but nothing is gated. */}
            <Route path="/" element={<Home />} />
            <Route path="/draft-overview" element={<DraftOverview />} />
            <Route path="/draft" element={<Draft />} />
            <Route path="/draft-complete" element={<DraftComplete />} />

            {/* Hidden — reachable only by typing /showcase or tapping the Home
                wordmark 5× quickly. Renders a pasted roster's Draft Complete
                view (viewOnly, never written to the leaderboard). */}
            <Route path="/showcase" element={<Showcase />} />

            {/* Streaks */}
            <Route path="/streak-overview" element={<StreakOverview />} />
            <Route path="/streak" element={<Streak />} />
            <Route path="/streak-complete" element={<StreakComplete />} />

            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/profile" element={<Profile />} />

            {/* Dynasty tab → rules page (not playable on web yet) */}
            <Route path="/dynasty" element={<ModeRules modeKey="dynasty" />} />

            {/* Roster Battles tab + sub-pages. Knockout and Pick Up are both live. */}
            <Route path="/battles" element={<RosterBattles />} />
            <Route path="/battles/pickup" element={<PickUp />} />
            <Route path="/battles/pickup/friends" element={<Friends />} />
            <Route path="/battles/pickup/matchmaking" element={<PickUpMatchmaking />} />
            <Route path="/battles/pickup/draft" element={<PickUpDraft />} />
            <Route path="/battles/pickup/complete" element={<PickUpComplete />} />
            <Route path="/battles/pickup/cpu" element={<PickUpCpu />} />
            <Route path="/battles/knockout" element={<Knockout />} />
            <Route path="/battles/knockout/draft" element={<KnockoutDraft />} />
            <Route path="/battles/knockout/complete" element={<KnockoutComplete />} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Layout>
      </KnockoutProvider>
    </AuthGate>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
